import {
  DEFAULT_WALL_COLOR,
  DEFAULT_ZONE_BORDER_COLOR,
  DEFAULT_ZONE_FILL_COLOR
} from "@gettactic/map-builder/src/components/map/defines";
import React from "react";
import { useViewport } from "../store/selectors";
import { GhostSvg } from "./GhostSvg";
import { type DraftShape, useOverlayValue } from "./overlayChannel";

const DRAFT_FILL = "rgba(0, 102, 255, 0.08)";
const DRAFT_STROKE = "#0066ff";

/** The shape being drawn. Fed by the active tool through the overlay
 *  channel, so only this component renders while the pointer moves. */
export const DraftLayer: React.FC = () => {
  const draft = useOverlayValue((state) => state.draft);
  const ghost = useOverlayValue((state) => state.ghost);
  const { scale } = useViewport();
  if (!draft && !ghost) return null;
  return (
    <g data-layer="draft" pointerEvents="none">
      {ghost ? <GhostSvg ghost={ghost} /> : null}
      {!draft ? null : renderDraft(draft, scale)}
    </g>
  );
};

const CLOSE_CUE_RADIUS = 7;

const renderDraft = (
  draft: NonNullable<ReturnType<typeof useOverlayValue<DraftShape | null>>>,
  scale: number
) => (
  <>
    {draft.kind === "rect" ? (
      <rect
        {...draft.rect}
        fill={DRAFT_FILL}
        stroke={DRAFT_STROKE}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    ) : null}
    {draft.kind === "ellipse" ? (
      <ellipse
        cx={draft.rect.x + draft.rect.width / 2}
        cy={draft.rect.y + draft.rect.height / 2}
        rx={draft.rect.width / 2}
        ry={draft.rect.height / 2}
        fill={DRAFT_FILL}
        stroke={DRAFT_STROKE}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    ) : null}
    {draft.kind === "zone" ? (
      <rect
        {...draft.rect}
        fill={DEFAULT_ZONE_FILL_COLOR}
        fillOpacity={0.12}
        stroke={DEFAULT_ZONE_BORDER_COLOR}
        strokeWidth={2}
        strokeDasharray="8 4"
      />
    ) : null}
    {draft.kind === "wall" ? (
      <line
        x1={draft.from.x}
        y1={draft.from.y}
        x2={draft.to.x}
        y2={draft.to.y}
        stroke={DEFAULT_WALL_COLOR}
        strokeWidth={draft.thickness}
        strokeLinecap="round"
        opacity={0.7}
      />
    ) : null}
    {draft.kind === "line" ? (
      <line
        x1={draft.from.x}
        y1={draft.from.y}
        x2={draft.to.x}
        y2={draft.to.y}
        stroke={DRAFT_STROKE}
        strokeWidth={1}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
      />
    ) : null}
    {draft.kind === "measure" ? <MeasureDraft draft={draft} /> : null}
    {draft.kind === "polyline" ? (
      <>
        <polyline
          points={[...draft.points, draft.to]
            .map((point) => `${point.x},${point.y}`)
            .join(" ")}
          fill="none"
          stroke={draft.color}
          strokeWidth={2}
          // Solid once the segment is locked to a 45° multiple.
          strokeDasharray={draft.isConstrained ? undefined : "6 4"}
          vectorEffect="non-scaling-stroke"
        />
        {draft.closeAt ? (
          <circle
            data-close-cue
            cx={draft.closeAt.x}
            cy={draft.closeAt.y}
            r={CLOSE_CUE_RADIUS / scale}
            fill="rgba(0, 102, 255, 0.25)"
            stroke={DRAFT_STROKE}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </>
    ) : null}
    {draft.kind === "freehand" && draft.points.length > 1 ? (
      <polyline
        points={draft.points.map((point) => `${point.x},${point.y}`).join(" ")}
        fill="none"
        stroke={draft.color}
        strokeWidth={2}
        strokeDasharray="5 5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    ) : null}
  </>
);

const MeasureDraft: React.FC<{
  draft: Extract<DraftShape, { kind: "measure" }>;
}> = ({ draft }) => {
  const pixels = Math.hypot(
    draft.to.x - draft.from.x,
    draft.to.y - draft.from.y
  );
  const label = draft.pixelsPerMeter
    ? `${(pixels / draft.pixelsPerMeter).toFixed(2)} m`
    : `${Math.round(pixels)} px`;
  return (
    <g>
      <line
        x1={draft.from.x}
        y1={draft.from.y}
        x2={draft.to.x}
        y2={draft.to.y}
        stroke="#7c3aed"
        strokeWidth={2}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={(draft.from.x + draft.to.x) / 2}
        y={(draft.from.y + draft.to.y) / 2 - 8}
        textAnchor="middle"
        fill="#7c3aed"
        fontSize={14}
        fontFamily="Quicksand"
        fontWeight={700}
      >
        {label}
      </text>
    </g>
  );
};
